/*
 * Purpose: Shared Super Intent entry — home hero + compose describe
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { FormEvent, KeyboardEvent, useCallback } from 'react';
import { ArrowUp, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { BrandMark } from '@/components/brand/BrandMark';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { AVERAGE_OUTCOME_SEEDS } from '@/seeds/averageOutcomes';
import { hasResolvedSecret } from '@/lib/resolveSecret';
import { ContextualError } from '@/components/errors/ContextualError';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { PRODUCT_TERMS, PRODUCT_BRAND } from '@/lib/productTerms';

export type SuperIntentVariant = 'home' | 'compose';

interface SuperIntentInterpreterProps {
  variant?: SuperIntentVariant;
  value: string;
  onChange: (text: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  error?: string | null;
  showExamples?: boolean;
  className?: string;
}

const EXAMPLE_OUTCOMES = AVERAGE_OUTCOME_SEEDS.map((s) => s.outcomeText);

export function SuperIntentInterpreter({
  variant = 'compose',
  value,
  onChange,
  onSubmit,
  disabled,
  isLoading,
  error,
  showExamples = variant === 'compose',
  className,
}: SuperIntentInterpreterProps) {
  const llmReady = hasResolvedSecret('OPENAI_API_KEY');
  const isHome = variant === 'home';

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      if (!value.trim() || disabled || isLoading) return;
      onSubmit();
    },
    [value, disabled, isLoading, onSubmit]
  );

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (isHome) {
    return (
      <form onSubmit={handleSubmit} className={cn('relative w-full max-w-2xl overflow-visible', className)}>
        <div
          className={cn(
            'ab-home-input-shell ui-field-action-group relative flex items-end gap-2 rounded-3xl border border-border/80 bg-card/90 backdrop-blur-md shadow-lg px-3 py-2 sm:px-4 sm:py-3'
          )}
        >
          <BrandMark size={28} className="shrink-0 mb-1.5 opacity-90 hidden sm:block" />
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Ask ${PRODUCT_BRAND}…`}
            rows={1}
            disabled={disabled || isLoading}
            className="ui-form-control flex-1 min-h-[2.75rem] max-h-32 resize-none border-0 shadow-none bg-transparent focus-visible:ring-0 text-base sm:text-lg placeholder:text-muted-foreground/70"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!value.trim() || disabled || isLoading}
            className="ui-field-action-btn shrink-0 rounded-full h-10 w-10 ui-lift ui-lift-accent mb-0.5"
            aria-label="Start Mission"
          >
            <ArrowUp className="h-5 w-5" />
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className={cn('ab-describe-outcome space-y-3', className)}>
      <p className="text-sm text-ab-ink-500 dark:text-ab-ink-400 leading-relaxed">
        Tell {PRODUCT_BRAND} what you want — not how to build it. The {PRODUCT_TERMS.autoBlocksFactory}{' '}
        assembles an {PRODUCT_TERMS.autoBlockFlow}; while running it&apos;s a Mission.
        {llmReady && (
          <Badge variant="secondary" className="ml-2 align-middle text-[10px] font-normal">
            AI planner ready
          </Badge>
        )}
      </p>
      <Textarea
        placeholder="e.g. When LinkedIn emails me jobs I care about, tailor my resume, apply, and track everything."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        rows={4}
        disabled={disabled || isLoading}
        aria-label="Describe your outcome"
        className="ab-describe-outcome-input ui-form-control min-h-[100px] text-base border-2 border-ab-green-core/35 focus-visible:border-ab-green-core/55 focus-visible:ring-2 focus-visible:ring-ab-green-core/20 focus-visible:ring-offset-1 focus-visible:ring-offset-background"
      />
      {showExamples && (
        <div className="ab-describe-suggestions space-y-1.5 pt-0.5">
          <p className="ab-describe-suggestions-label text-muted-foreground">Suggestions — tap to fill</p>
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLE_OUTCOMES.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => onChange(ex)}
                className="ab-describe-suggestion"
              >
                {ex.length > 72 ? `${ex.slice(0, 72)}…` : ex}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && (
        <ContextualError
          message={error}
          action={
            isLoading
              ? undefined
              : {
                  label: 'Try again',
                  onClick: () => handleSubmit(),
                }
          }
          secondaryAction={{ label: 'Open Setup', href: REMEDIATION.setup }}
        />
      )}
      <Button
        onClick={() => handleSubmit()}
        disabled={!value.trim() || disabled || isLoading}
        className="w-full sm:w-auto"
      >
        <Pencil className="h-4 w-4 mr-2" />
        {isLoading ? 'Interpreting…' : 'Interpret outcome'}
      </Button>
    </div>
  );
}
